/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import {useTranslation} from "react-i18next";  // Importing the translation hook for i18n support
import {useDispatch, useSelector} from "react-redux";  // Importing Redux hooks for dispatch and selector usage
import {useEffect, useState} from "react";  // Importing React hooks for component state and side effects
import {useNavigate} from "react-router-dom";  // Importing the useNavigate hook for navigation

// Importing API actions and constants related to the Announcement module
import {
    AnnouncementTypes, CreateAnnouncement,
    GetEmployeesPageable, SendByTopicScope, SendToAllScope, SendToSomeEmployees,
} from "../../../../../reduxToolkit/Announcement/AnnouncementApi";

import routes from "../../../../../Routes/routes";  // Importing the routes for navigation
import {NOTIFICATION_MODEL, NOTIFICATION_TYPE_MODEL, PEOPLE_MODEL} from "./NotificationModel";  // Importing models for notification data

import CustomPagination from "../../../../../components/table/CustomPagination";  // Custom pagination component
import {toast} from "react-toastify";
import {USER_TYPE_NAME_MODEL} from "../../../../../enums/userTypeEnum";
import {getPhotoPicker, PHOTO_PICKER_MODEL} from '../../../../../components/PhotoPicker/SubComponent/Models/Models';  // Importing toast notifications for feedback messages
import { UploadImage } from "../../../../../reduxToolkit/ShareSlice/shareApi";

// Main functional component for creating notifications
const CreateNotificationsApp = () => {
    const {t} = useTranslation();  // Translation function for i18n
    const navigate = useNavigate();  // Hook to navigate between routes
    const dispatch = useDispatch();  // Redux dispatch hook
    const { data } = useSelector((state) => state?.authenticatioauthennSlice?.user);  // Getting user data from Redux store
     const employeesData = useSelector((state) => state?.AnnouncementSlice?.employeesData);  // Getting employees data from Redux store

    // State variables for notification form inputs
    const [title, setTitle] = useState("");  // State for the notification title
    const [message, setMessage] = useState(null);  // State for the notification message
    const [time, setTime] = useState(null);  // State for the meeting time
    const [date, setDate] = useState(null);  // State for the meeting date
    const [type, setType] = useState("");  // State for the notification type
    const [selectedScope, setSelectedScope] = useState(null);  // State for the selected audience scope
    const [selectedType, setSelectedType] = useState(null);  // State for the selected notification type
    const [selectedEmployees, setSelectedEmployees] = useState([]);  // State for selected employees in case of custom scope
    const [emptyFields, setEmptyFields] = useState(false);  // State to track if any required fields are empty
    const [fileType, setFileType] = useState(false);  // State to toggle the file type (image or PDF)
    const [notificationTypeList, setNotificationTypeList] = useState([]);  // State to toggle the file type (image or PDF)
    const [photoPickerPdf, setPhotoPickerPdf] = useState({...PHOTO_PICKER_MODEL}) // State for the file (PDF) to upload
    const [photoPickerFile, setPhotoPickerFile] = useState({...PHOTO_PICKER_MODEL}) // State for company image preview
    // Using custom pagination hook for employees data
    const { pagination, page, rowsPerPage } = CustomPagination({
        fixed: false,
        content: employeesData,
        labelRowsPerPage: 'employees_per_page'
    });

    // Effect hook to dispatch action for fetching notification types on component mount
    useEffect(() => {
        dispatch(AnnouncementTypes()).then(({payload:{data:{data}}}) => {
            if(data?.length > 0){
                let aux = [];
                data.map(e => {
                    aux.push({id:e.id, name:t(e.name)});
                });
                setNotificationTypeList(aux);
            }
        });
    }, []);

    // Effect hook to dispatch action for fetching employees data with pagination
    useEffect(() => {
        const body = {
            order: true,
            page: page,
            size: rowsPerPage,
            sortBy: 'id',
        };
        dispatch(GetEmployeesPageable(body));
    }, [page, rowsPerPage]);

    // Handles employee selection/deselection in the checkbox list
    const handleCheckBox = (e, item) => {
        const { checked } = e.target;
        if (checked) {
            setSelectedEmployees([...selectedEmployees, item.id]);  // Add employee to selected list
        } else {
            setSelectedEmployees((selectedEmployees) =>
                selectedEmployees.filter((selectedId) => selectedId !== item.id),  // Remove employee from selected list
            );
        }
    };

    // Function to upload either image or file (PDF) to the server after announcement creation
    const uploadImageOrFileToServer = (announcementId) => {
        let image = getPhotoPicker(photoPickerFile);
        if (image) {
            let formData = new FormData();
            formData.append('id', announcementId);
            formData.append('option', 'announcement_image');
            formData.append('file', image);
            dispatch(UploadImage(formData)).then(() => {
                navigate(routes.announcement.list);  // Redirect to announcement list after upload
                toast.success(t('has_been_created_successfully'));
            });
        }
        let pdfFile =  getPhotoPicker(photoPickerPdf);
        if (pdfFile) {
            let formData = new FormData();
            formData.append('id', announcementId);
            formData.append('option', 'announcement_file');
            formData.append('file', pdfFile);
            dispatch(UploadImage(formData)).then(() => {
                navigate(routes.announcement.list);  // Redirect to announcement list after upload
                toast.success(t('has_been_created_successfully'));
            });
        }
    };

    // Function to create a new notification
    const handleCreate = () => {
        const body =
            selectedType === NOTIFICATION_TYPE_MODEL.AD.id
                ? NOTIFICATION_MODEL.set({
                    message,
                    notificationTypeId: selectedType,
                    userId: data?.id,
                    title,
                })
                : selectedType === NOTIFICATION_TYPE_MODEL.MEETING.id
                    ? NOTIFICATION_MODEL.set({
                        message,
                        notificationTypeId: selectedType,
                        userId: data?.id,
                        title,
                        dateMeeting: date?.getTime()
                    })
                    : selectedType ===  NOTIFICATION_TYPE_MODEL.OTHER.id
                        ? NOTIFICATION_MODEL.set({
                            message,  // Placeholder message
                            notificationTypeId: selectedType,
                            userId: data?.id,
                            title,
                            type: type
                        })
                        : null;

        // Validation check for required fields
        if (body === null
            || !body.isValid()
            || (selectedType === NOTIFICATION_TYPE_MODEL.MEETING.id && (!time || !date))
            || (selectedType === NOTIFICATION_TYPE_MODEL.OTHER.id && !type)
            || selectedScope === null
            || (selectedScope === PEOPLE_MODEL.CHOOSE_SOME_EMPLOYEES && selectedEmployees.length === 0)
        ) {
            setEmptyFields(true);  // Set empty fields error flag
            if (selectedScope === PEOPLE_MODEL.CHOOSE_SOME_EMPLOYEES && selectedEmployees.length === 0) {
                toast.warning(t('must_select_at_least_one_employee'));  // Show error message if no employee is selected
            }
            return;
        }

        setEmptyFields(false);  // Reset empty fields flag
        let pdfFile =  getPhotoPicker(photoPickerPdf);
        let image = getPhotoPicker(photoPickerFile);
        dispatch(CreateAnnouncement(body)).then(
            ({ payload: { data: { data, success } } }) => {
                if (success) {
                    // If notification creation is successful, send to different scopes
                    if (selectedScope === PEOPLE_MODEL.ALL_EMPLOYEES_CONTRACTORS_SUPPLIERS) {
                        dispatch(SendToAllScope(data?.id)).then(() => {
                            if (!pdfFile && !image) {
                                navigate(routes.announcement.list);
                                toast.success(t('has_been_created_successfully'));
                            } else {
                                uploadImageOrFileToServer(data?.id);
                            }
                        });
                    } else if (selectedScope === PEOPLE_MODEL.ONLY_EMPLOYEES || selectedScope === PEOPLE_MODEL.ONLY_CONTRACTORS || selectedScope === PEOPLE_MODEL.ONLY_SUPPLIERS) {
                        const body = {
                            announcementId: data?.id,
                            topic:
                                selectedScope === PEOPLE_MODEL.ONLY_EMPLOYEES
                                    ? USER_TYPE_NAME_MODEL.EMPLOYEE
                                    : selectedScope === PEOPLE_MODEL.ONLY_SUPPLIERS
                                        ? USER_TYPE_NAME_MODEL.PROVIDER
                                        : selectedScope === PEOPLE_MODEL.ONLY_CONTRACTORS
                                            ? USER_TYPE_NAME_MODEL.CONTRACTOR
                                            : '',
                        };
                        dispatch(SendByTopicScope(body)).then(() => {
                            if (!pdfFile && !image) {
                                navigate(routes.announcement.list);
                                toast.success(t('has_been_created_successfully'));
                            } else {
                                uploadImageOrFileToServer(data?.id);
                            }
                        });
                    } else if (selectedScope === PEOPLE_MODEL.CHOOSE_SOME_EMPLOYEES) {
                        const body = {
                            ids: selectedEmployees,
                            announcementId: data?.id,
                        };
                        dispatch(SendToSomeEmployees(body)).then(() => {
                            if (!pdfFile && !image) {
                                navigate(routes.announcement.list);
                                toast.success(t('has_been_created_successfully'));
                            } else {
                                uploadImageOrFileToServer(data?.id);
                            }
                        });
                    }
                }
            },
        );
    };

    // Switch handler for toggling file upload type (image vs PDF)
    const handleSwitch = (e) => {
        setFileType(e.target.checked);  // Set file type (true for image, false for PDF)
    };

    return {
        t, // Translation function
        emptyFields,
        title,
        setTitle,
        selectedType,
        setSelectedType,
        message,
        setMessage,
        notificationTypeList,
        time,
        setTime,
        date,
        setDate,
        type,
        setType,
        selectedScope,
        setSelectedScope,
        fileType,
        selectedEmployees,
        employeesData,
        pagination,
        handleCheckBox,
        handleSwitch,
        handleCreate,
        photoPickerFile,
        setPhotoPickerFile,
        photoPickerPdf,
        setPhotoPickerPdf
    };
}

export default CreateNotificationsApp;
